import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { aciesDateFormat } from '@shared/helpers';
import { GenericTableColumn } from '@shared/models/generictablecolumn';
import { NotificationService } from '@shared/services/notification.service';
import { UserService } from '@shared/services/user.service';
import { filter, Subject, switchMap, take, takeUntil } from 'rxjs';
import { IMonitoringEnvironment, IMonitoringErrorRow, IMonitoringOverview } from './monitoring.models';
import { MonitoringService } from './monitoring.service';
import { accountName, auditTone, flattenTree, IAttentionLine, IMonitoringTreeRow, IRowCounts, MONITORING_PRESETS, MonitoringPreset, needsAttention, presetParams } from './monitoring.utils';

const TRANSLATION_NS = 'setup.cross-account-one-monitoring';
const KIND_NS = 'administration.sync-monitoring.audit.kinds';
const AUDIT_STATUS_NS = 'administration.sync-monitoring.audit.statuses';

/** A row as the table shows it: a tree row with its main cause as a readable label (the raw kind as its tooltip), or the loading or retry row under an environment. */
interface ITreeDisplayRow extends Partial<IRowCounts> {
	id: string;
	level: IMonitoringTreeRow['level'] | 'status';
	name: string;
	link: string;
	/** Open, closed, or null for no toggle; the retry row is closed. */
	expanded: boolean | null;
	mainCauseLabel?: string;
	/** An environment row's last full audit pass, formatted; empty on other rows. */
	lastFullPass?: string;
	/** The audit status as a badge. */
	auditLabel?: string;
}

/** An environment's table load: in flight or failed. `token` tells a current response from one for an earlier preset. */
interface ITableLoad {
	state: 'loading' | 'failed';
	token: number;
}

function escapeHtml(text: string): string {
	return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

@Component({
	selector: 'app-cross-account-one-monitoring',
	templateUrl: './cross-account-one-monitoring.component.html',
	styleUrls: ['./cross-account-one-monitoring.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CrossAccountOneMonitoringComponent implements OnInit, OnDestroy {
	public readonly presets = MONITORING_PRESETS;
	public preset: MonitoringPreset = 'allCurrent';
	public overview: IMonitoringOverview | null = null;
	public isLoading = true;
	public noAccess = false;
	public attentionLines: IAttentionLine[] = [];
	public remainingRetryingTables = 0;
	public tableSource = new MatTableDataSource<ITreeDisplayRow>([]);
	public tableColumns: GenericTableColumn[] = [
		{ name: `${TRANSLATION_NS}.columns.name`, dataKey: 'name', width: 22 },
		{ name: `${TRANSLATION_NS}.columns.needs-person`, dataKey: 'terminal', width: 11 },
		{ name: `${TRANSLATION_NS}.columns.retrying`, dataKey: 'retrying', width: 11 },
		{ name: `${TRANSLATION_NS}.columns.outdated`, dataKey: 'outdated', width: 11 },
		{ name: `${TRANSLATION_NS}.columns.main-cause`, dataKey: 'mainCauseLabel', width: 14 },
		{ name: `${TRANSLATION_NS}.columns.audit`, dataKey: 'auditLabel', width: 12 },
		{ name: `${TRANSLATION_NS}.columns.last-full-pass`, dataKey: 'lastFullPass', width: 13 },
		{ name: `${TRANSLATION_NS}.columns.open`, dataKey: 'link', isLink: true, linkTitle: `${TRANSLATION_NS}.open`, linkIcon: 'acies_launch', linkOpener: true, width: 6 },
	];

	private accounts: { id: string; name: string }[] = [];
	/** Open account and environment row ids. */
	private expanded = new Set<string>();
	/** Loaded tables by environment row id, for the current preset. */
	private tables = new Map<string, IMonitoringErrorRow[]>();
	private loads = new Map<string, ITableLoad>();
	private loadToken = 0;
	private destroy$ = new Subject<void>();

	constructor(
		private monitoringService: MonitoringService,
		private userService: UserService,
		private notificationService: NotificationService,
		private translateService: TranslateService,
	) {
		// The tree keeps its own order: a header click must not sort the flattened rows.
		this.tableSource.sortData = (rows) => rows;
	}

	ngOnInit(): void {
		this.userService.accountsSubject$.pipe(takeUntil(this.destroy$)).subscribe((accounts) => {
			this.accounts = (accounts ?? []).map((account) => ({ id: account.id, name: `${account.organization.name} · ${account.name}` }));
			this.render();
		});
		this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => this.render());

		// The overview needs no selected account, only the user's accounts for its request header (see MonitoringService).
		this.userService.accountsSubject$
			.pipe(
				filter((accounts) => !!this.userService.account || accounts.length > 0),
				take(1),
				switchMap(() => this.monitoringService.getOverview()),
				takeUntil(this.destroy$),
			)
			.subscribe({
				next: (overview) => {
					this.isLoading = false;
					this.overview = overview;
					({ lines: this.attentionLines, remainingRetryingTables: this.remainingRetryingTables } = needsAttention(overview));
					this.expanded = new Set();
					this.render();
				},
				error: (error: unknown) => {
					this.isLoading = false;
					if (error instanceof HttpErrorResponse && error.status === 403) {
						this.noAccess = true;
						return;
					}
					console.error('Cross-account monitoring overview failed', error);
					this.notificationService.notify([`${TRANSLATION_NS}.errors.overview-load-failed`], [], 'error');
				},
			});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	get oldestPassDisplay(): string {
		return this.overview?.oldestPassStartedAt ? aciesDateFormat(new Date(this.overview.oldestPassStartedAt), true) : '';
	}

	/** The three group tiles, each applying its own preset. */
	get tiles(): { preset: MonitoringPreset; counts: IMonitoringOverview['totals']['terminal'] }[] {
		if (!this.overview) {
			return [];
		}
		const { terminal, retrying, outdated } = this.overview.totals;
		return [
			{ preset: 'needsPerson', counts: terminal },
			{ preset: 'retrying', counts: retrying },
			{ preset: 'outdated', counts: outdated },
		];
	}

	get staleAccountNames(): string {
		return (this.overview?.staleAccounts ?? []).map((stale) => accountName(this.accounts, stale.accountId)).join(', ');
	}

	accountNameOf(environment: IMonitoringEnvironment): string {
		return accountName(this.accounts, environment.accountId);
	}

	/** The kind's label, or the raw kind when it has no translation. */
	kindLabel(kind: string): string {
		const key = `${KIND_NS}.${kind}`;
		const label = this.translateService.instant(key);
		return label === key ? kind : label;
	}

	/** The audit status as One Monitor labels it, or the raw status when it has no translation. */
	auditStatusLabel(status: string): string {
		const key = `${AUDIT_STATUS_NS}.${status}`;
		const label = this.translateService.instant(key);
		return label === key ? status : label;
	}

	/** The badge tone of an audit status, for the template. */
	auditTone(status: string): string {
		return auditTone(status);
	}

	/** Applies a preset: the loaded tables belonged to the old one, so they are dropped and the open environments load again. */
	selectPreset(preset: MonitoringPreset): void {
		if (preset !== this.preset) {
			this.preset = preset;
			this.tables.clear();
			this.loads.clear();
		}
		this.render();
	}

	/** "Show in list": applies the line's preset and opens its environment. */
	showInList(line: IAttentionLine): void {
		this.expanded.add(`account:${line.environment.accountId}`);
		this.expanded.add(`environment:${line.environment.accountId}/${line.environment.environmentId}`);
		this.selectPreset(line.preset);
	}

	onRowToggle(row: ITreeDisplayRow): void {
		if (row.level === 'status') {
			// The retry row of an environment whose tables failed to load.
			this.loads.delete(row.id.slice('status:'.length));
		} else if (this.expanded.has(row.id)) {
			this.expanded.delete(row.id);
		} else {
			this.expanded.add(row.id);
		}
		this.render();
	}

	rowExpanded = (row: ITreeDisplayRow): boolean | null => row.expanded;

	rowClass = (row: ITreeDisplayRow): string => `cam-row-${row.level}`;

	/** Rebuilds the visible rows. First drops the loads of environments no longer open and shown, so their late responses are ignored and opening them again loads anew, then starts the table load of every open environment that has neither tables nor a load. */
	private render(): void {
		if (!this.overview) {
			return;
		}
		const open = this.tree().filter((row) => row.level === 'environment' && row.expanded);
		for (const id of this.loads.keys()) {
			if (!open.some((row) => row.id === id)) {
				this.loads.delete(id);
			}
		}
		for (const row of open) {
			if (!this.tables.has(row.id) && !this.loads.has(row.id)) {
				this.loadTables(row);
			}
		}
		this.tableSource.data = this.tree().flatMap((row) => {
			const load = row.level === 'environment' && row.expanded ? this.loads.get(row.id) : undefined;
			return load ? [this.displayRow(row), this.statusRow(row, load)] : [this.displayRow(row)];
		});
	}

	private tree(): IMonitoringTreeRow[] {
		return flattenTree(this.overview, this.preset, this.expanded, this.tables, this.accounts);
	}

	private loadTables(row: IMonitoringTreeRow): void {
		const token = ++this.loadToken;
		const environment = row.environment;
		this.loads.set(row.id, { state: 'loading', token });
		this.monitoringService
			.getEnvironmentErrors(environment.accountId, environment.environmentId, presetParams(this.preset, environment))
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (rows) => {
					if (this.loads.get(row.id)?.token !== token) {
						return;
					}
					this.loads.delete(row.id);
					this.tables.set(row.id, rows);
					this.render();
				},
				error: () => {
					if (this.loads.get(row.id)?.token !== token) {
						return;
					}
					this.loads.set(row.id, { state: 'failed', token });
					this.render();
				},
			});
	}

	private displayRow(row: IMonitoringTreeRow): ITreeDisplayRow {
		const mainCauseLabel = row.mainCause ? `<span title="${escapeHtml(row.mainCause)}">${escapeHtml(this.kindLabel(row.mainCause))}</span>` : '';
		const lastFullPassAt = row.environment?.lastFullPassAt;
		const lastFullPass = lastFullPassAt ? aciesDateFormat(new Date(lastFullPassAt), true) : row.environment ? '—' : '';
		const auditLabel = row.auditStatus ? `<span class="cam-audit cam-audit-${auditTone(row.auditStatus)}">${escapeHtml(this.auditStatusLabel(row.auditStatus))}</span>` : '';
		return { id: row.id, level: row.level, name: escapeHtml(row.name), link: row.link, expanded: row.expanded, terminal: row.terminal, retrying: row.retrying, outdated: row.outdated, mainCauseLabel, lastFullPass, auditLabel };
	}

	/** The loading or retry row under an environment. */
	private statusRow(environment: IMonitoringTreeRow, load: ITableLoad): ITreeDisplayRow {
		return { id: `status:${environment.id}`, level: 'status', name: this.translateService.instant(`${TRANSLATION_NS}.tables-${load.state}`), link: environment.link, expanded: load.state === 'failed' ? false : null };
	}
}
